import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FieldMetaState, useField } from 'react-final-form'
import { Option } from '@cargomart/ui'
import { FieldValidator } from 'final-form'
import { checkFieldError } from 'helpers/form'
import { handleFetchResponse } from 'src/api/errors'
import { useGetCompanyMutation } from 'src/modules/customer/api'
import { SuggestionCompanyItem } from 'src/schema'

import { BaseAsyncSelect } from '../components/Select/BaseAsyncSelect'
import { IBaseAsyncSelect } from '../components/Select/types'

interface IProps
  extends Pick<
    IBaseAsyncSelect<SuggestionCompanyItem>,
    'placeholder' | 'size' | 'isClearable' | 'isDisabled' | 'className' | 'maxOptionsToDisplay' | 'maxOptionListHeight'
  > {
  name: string
  calculateError?: (meta: FieldMetaState<SuggestionCompanyItem>) => string | boolean | undefined
  className?: string
  handleErrorSearch?: () => void
  handleSearch?: () => void
  hasSearchIcon?: boolean
  mobileName?: string
  onPickOption?: (option?: SuggestionCompanyItem) => void
  validate?: FieldValidator<SuggestionCompanyItem>
}

const getOptionName = (option?: SuggestionCompanyItem) => option?.value || ''

export const MIN_QUERY_LENGTH = 3

export const FieldCompanySelect = memo(function FieldCompanySelect({
  className,
  name,
  placeholder = 'Название или ИНН',
  mobileName = placeholder,
  size = 'nm',
  isClearable = true,
  hasSearchIcon = true,
  onPickOption,
  calculateError = checkFieldError,
  validate,
  handleErrorSearch,
  handleSearch,
  ...props
}: IProps) {
  const { input, meta } = useField<SuggestionCompanyItem>(name, { validate })
  const { value: selectedOption, onChange } = input
  const defaultValue = useRef(selectedOption)
  const lastSearch = useRef('')

  const [loadOptions, { data, isLoading }] = useGetCompanyMutation()
  const [formatedData, setFormatedData] = useState<Array<SuggestionCompanyItem & { hash: string }>>([])
  useEffect(() => {
    setFormatedData(data?.map((item) => ({ ...item, id: item.value, hash: item.id })) || [])
  }, [data])

  const handleRequestOptions = useCallback(
    (searchInfo: { search: string }) => {
      handleSearch && handleSearch()
      const newSearch = searchInfo.search || selectedOption?.inn || ''
      if (lastSearch.current === newSearch) {
        return
      }
      lastSearch.current = newSearch

      let res = Promise.reject()
      if (newSearch.trim().length >= MIN_QUERY_LENGTH) {
        res = loadOptions(newSearch) as unknown as Promise<never>
        res.then(
          handleFetchResponse(
            (res) => {
              if (res === null) {
                handleErrorSearch && handleErrorSearch()

                return
              }
              handleSearch && handleSearch()
            },
            () => handleErrorSearch && handleErrorSearch()
          )
        )
      } else {
        setFormatedData([])
      }

      return res as Promise<void>
    },
    [selectedOption, loadOptions]
  )

  const handleChange = useCallback(
    (option?: SuggestionCompanyItem) => {
      onChange(option)
      onPickOption?.(option)
    },
    [onChange, onPickOption]
  )

  const error = useMemo(() => calculateError(meta), [meta, calculateError])

  return (
    <BaseAsyncSelect<SuggestionCompanyItem>
      {...props}
      className={className}
      defaultOption={defaultValue.current}
      error={error}
      fullscreenName={mobileName}
      isClearable={isClearable}
      isLoading={isLoading}
      noOptionsMessageWithEmptySearch="Начните вводить для поиска требуется 3 символа"
      optionsLength={formatedData?.length}
      placeholder={selectedOption ? getOptionName(selectedOption) : placeholder}
      prefix={undefined}
      size={size}
      validateForEmpty={(value) => value?.length > MIN_QUERY_LENGTH}
      value={getOptionName(selectedOption) as unknown as SuggestionCompanyItem}
      onChange={handleChange}
      // @ts-ignore
      onRequestOptions={handleRequestOptions}
    >
      {formatedData?.map((option) => (
        <Option
          key={option.inn}
          data-test-id={`customer-page--create--${option.hash}`}
          description={option.inn}
          title={option.value}
          value={option}
        />
      ))}
    </BaseAsyncSelect>
  )
})
